/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import FormFader from "../Common/Fader/FormFader";
import wizardStyle from "../wizardStyle";
import { Box, Paper } from "@mui/material";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import FormLegend from "../Common/FormLegend/FormLegend";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { MediaObjectCombinationRequest } from "../../../types/Entities/MediaObject/MediaObjectCombinationRequest";
import { apiPatch } from "../../../services/apiClient";
import { getProductData } from "../../../services/productService";
import useMediaObjectCombinationRequest from "../../../hooks/useMediaObjectCombinationRequest";
import { fetchCollection, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import { useForm } from "../../../provider/Form/FormProvider";
import { DocumentsFormConfig, getDocumentsFormConfig } from "../../../services/documentsFormService";
import { MediaObject } from "../../../types/Entities/MediaObject/MediaObject";
import { SectionElements, SectionElementsProps } from "./SectionElements";
import { SectionHeadline } from "./SectionHeadline";

export type DocumentsFormProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};
export const DocumentsForm = ({ submitProps, product }: DocumentsFormProps) => {
  const [uploadIsBlocked, setUploadIsBlocked] = useState(false);
  const { values, updateValue, handleSubmit } = useForm();
  const queryClient = useQueryClient();
  const { requiredDocuments, optionalDocuments, headline, stepHeadline } = getDocumentsFormConfig({ product, submitProps });

  const urlParams = {
    product: product.name,
    productId: product.id,
  };

  function getDocumentTypes(documentFormConfig: DocumentsFormConfig[]) {
    return documentFormConfig
      .map((formConfig) => formConfig.mediaObjectTypeName || (formConfig.type === "FileUploadSection" ? formConfig.fieldName : formConfig.fieldname))
      .filter(Boolean);
  }

  const requiredDocumentTypes = getDocumentTypes(requiredDocuments);

  const optionalDocumentTypes = getDocumentTypes(optionalDocuments);

  const allTypes = [...requiredDocumentTypes, ...optionalDocumentTypes];

  const visibleFileUploadSections = [...requiredDocuments, ...optionalDocuments]
    .filter((document) => document.type === "FileUploadSection" && !document.isHidden?.({ product }))
    .map((document) => (document.type === "FileUploadSection" ? document.mediaObjectTypeName || document.fieldName : ""));

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction, hasAtLeastOneFileOfType } = useMediaObjectCombinationRequest(
    product,
    visibleFileUploadSections
  );

  const productApiUri = getProductData(product.name, "apiUri");

  function refreshMediaObjectCollection() {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/media_objects"),
    });
  }

  async function refreshCase() {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.item(productApiUri, product.id),
    });
  }

  const { data: mediaObjectCollection, isLoading } = useQuery<MediaObject[]>({
    queryKey: queryKeys.collection("/media_objects", urlParams),
    queryFn: () =>
      fetchCollection("/media_objects", urlParams)
  });

  function checkIsReady() {
    const missingRequiredFiles = requiredDocumentTypes.filter(
      (type) =>
        ![
          ...(values.missingDocuments ?? []),
          ...product.mediaObjectCombinationRequests
            ?.filter((mediaObjectCombinationRequest: MediaObjectCombinationRequest) => allTypes.includes(mediaObjectCombinationRequest.type))
            .map((mediaObjectCombinationRequest: MediaObjectCombinationRequest) => {
              if (mediaObjectCombinationRequest.filesToCombine?.[0].files.length) {
                return mediaObjectCombinationRequest.type;
              }
            }),
          ...(mediaObjectCollection ?? []).filter((mediaObject) => allTypes.includes(mediaObject.fieldname)).map((mediaObject) => mediaObject.fieldname),
        ].includes(type)
    );

    return !isLoadingMediaObjectCombinationRequest && missingRequiredFiles.length === 0;
  }

  async function handleMissingDocumentCheckboxChange(documentType: string) {
    let newMissingDocuments: string[] = [];

    if (values?.missingDocuments?.includes(documentType)) {
      newMissingDocuments = [...values.missingDocuments.filter((docType: string) => docType !== documentType)];
    } else if (Array.isArray(values?.missingDocuments)) {
      newMissingDocuments = [...values.missingDocuments, documentType];
    } else {
      newMissingDocuments = [documentType];
    }

    await updateProductMissingDocuments(newMissingDocuments.filter((type) => requiredDocumentTypes.includes(type)));
  }

  async function updateProductMissingDocuments(newMissingDocuments: string[]) {
    const oldValues = { ...values };
    updateValue("missingDocuments", newMissingDocuments);

    await apiPatch(getProductData(product.name, "apiUri") + "/" + product.id, {
      missingDocuments: newMissingDocuments.filter(Boolean),
    }).catch(() => {
      updateValue("missingDocuments", oldValues?.missingDocuments);
    });
  }

  if (isLoading || isLoadingMediaObjectCombinationRequest || !mediaObjectCollection) {
    return <LegalbirdLoader centered />;
  }

  const sectionElementProps: Omit<SectionElementsProps, "configs"> = {
    product,
    mediaObjectCollection,
    mediaObjectCombinationRequests,
    updateAction,
    hasAtLeastOneFileOfType,
    onMissingDocumentsCheckboxChange: handleMissingDocumentCheckboxChange,
    refreshCase,
    refreshMediaObjectCollection,
    setUploadIsBlocked,
    uploadIsBlocked,
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant="h4" sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            {stepHeadline}
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
              marginBottom: "15px",
            }}
          >
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{headline}</FormLegend>
            </Grid>
            {Boolean(requiredDocuments.length) && (
              <>
                <SectionHeadline headline={"Notwendige Dokumente"} />
                {<SectionElements {...sectionElementProps} configs={requiredDocuments} />}
              </>
            )}
            {Boolean(optionalDocuments.length) && (
              <>
                {Boolean(requiredDocuments.length) && <SectionHeadline headline={"Weitere Dokumente"} marginTop={"48px"} />}
                {<SectionElements {...sectionElementProps} configs={optionalDocuments} />}
              </>
            )}
            <Grid size={12}>
              <FormSubmit {...submitProps} disabled={!checkIsReady()} sx={{ marginTop: "32px" }} />
            </Grid>
          </Grid>
        </Box>
      </FormFader>
    </Paper>
  );
};
